"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useSWR from "swr";
import { useSearchParams, useRouter } from "next/navigation";
import { Item, CategorySelection } from "@/lib/types";
import { categoryLabels } from "@/lib/categoryLabels";
import { useActiveCategoryStructure } from "@/lib/hooks/useActiveCategoryStructure";
import {
  getApprovedItems,
  getItemsByCategory,
  getItemsByLocation,
  searchItems,
  getRelatedSearchItems,
} from "@/lib/itemsAPI";
import { useLocation } from "@/contexts/LocationContext";
import { useAuth } from "@/contexts/AuthContext";
import { useSystemSettings } from "@/contexts/SystemSettingsContext";
import { useItemShareCard } from "@/lib/hooks/useItemShareCard";
import { useItemPreview } from "@/lib/hooks/useItemPreview";
import type { SearchableSelectOption } from "@/components/ui/SearchableSelect";
import type { SearchResponse } from "@/lib/services/searchService";
import {
  LAGOS_LGAS,
  OYO_LGAS,
  LAGOS_NEIGHBORHOODS,
  OYO_NEIGHBORHOODS,
} from "@/lib/locationUtils";
import {
  getUserStateAndLGA,
  getLocationTier,
  orderItemsByTier,
  type LocationTier,
} from "@/lib/locationHierarchy";
import { selectEligibleMarketItems } from "@/lib/market/eligibility";
import {
  compareWithinTierWithAvailabilityBoost,
  type AvailabilitySortBoostOptions,
} from "@/lib/items/sellerAvailabilityConfirmed";
import { trackMetaSearch } from "@/lib/metaPixel";
import { trackClaritySearch } from "@/lib/clarity";
import type { ProcessedItem, PriceRange } from "@/lib/market/types";
import {
  buildActiveFilterChips,
  buildCategoryQuickPicks,
  buildSubcategoryPicks,
  buildVisibleCategoryChips,
  readInitialMarketState,
  serializeCategoryParams,
  serializeClearAllFilters,
  serializeLocationParam,
  serializePriceParams,
  serializeSearchParam,
  serializeSortParam,
  type MarketFilterChipKind,
} from "@/lib/market/browseState";

const ITEMS_PER_PAGE = 20;

/**
 * Max items fetched for an unfiltered default browse. When a browse hits this
 * cap we surface an honest "search or pick a category for more" notice (D3).
 */
const CATALOG_BROWSE_LIMIT = 100;

/** Cap how many items get distance calculation on Market to limit distance-matrix API calls. */
const MARKET_MAX_ITEMS_FOR_DISTANCE = 50;

// Prevent overlapping/redundant Market distance-processing runs.
// In dev (React StrictMode) effects can run more than once; we keep a single
// in-flight execution per signature to avoid repeated distance-matrix bursts.
const GLOBAL_MARKET_DISTANCE_IN_FLIGHT_KEY = "__sio_marketDistanceInFlight__";
const GLOBAL_MARKET_LAST_DISTANCE_SIGNATURE_KEY =
  "__sio_marketLastDistanceSignature__";

function getGlobalMarketDistanceInFlight(): boolean {
  return Boolean(
    (globalThis as unknown as Record<string, unknown>)[
      GLOBAL_MARKET_DISTANCE_IN_FLIGHT_KEY
    ],
  );
}

function setGlobalMarketDistanceInFlight(value: boolean): void {
  (globalThis as unknown as Record<string, unknown>)[
    GLOBAL_MARKET_DISTANCE_IN_FLIGHT_KEY
  ] = value;
}

function getGlobalMarketLastDistanceSignature(): string | null {
  const v = (globalThis as unknown as Record<string, unknown>)[
    GLOBAL_MARKET_LAST_DISTANCE_SIGNATURE_KEY
  ];
  return typeof v === "string" ? v : null;
}

function setGlobalMarketLastDistanceSignature(signature: string): void {
  (globalThis as unknown as Record<string, unknown>)[
    GLOBAL_MARKET_LAST_DISTANCE_SIGNATURE_KEY
  ] = signature;
}

/**
 * Owns all market browse orchestration: filter state, URL sync, SWR fetching,
 * distance/tier processing, related-search, infinite scroll, and derived UI
 * flags. Presentational components (MarketView and children) consume this hook.
 *
 * Phase 1 note: logic is moved verbatim from the former market/page.tsx to
 * preserve exact behavior. UX changes land in later phases via this owner.
 */
export function useMarketBrowse() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const { userLocation, calculateDistancesAsync, isLoadingLocation } =
    useLocation();
  const {
    includeOtherLocationTier,
    newListingNearMeNotificationsEnabled,
    homepageDiscoveryGranularity,
    homepageDiscoveryAutoWidenEnabled,
    buyerAvailabilityConfirmedSortBoostEnabled,
    buyerAvailabilityConfirmedWindowDays,
    isLoading: settingsLoading,
  } = useSystemSettings();
  const { user, isAuthenticated } = useAuth();
  const itemShare = useItemShareCard();
  // Quick-view (preview modal) state — shared owner so market matches homepage.
  const itemPreview = useItemPreview();
  const { activeCategoryStructure } = useActiveCategoryStructure();
  const [page, setPage] = useState(1);
  const [selectedCategory, setSelectedCategory] =
    useState<CategorySelection | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const lastTrackedMetaSearchRef = useRef<string | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<string>("");
  const [sortBy, setSortBy] = useState<string>("newest");
  // True once the buyer's sort choice is explicit (URL param or manual toggle),
  // so the D2 proximity default never overrides an intentional selection.
  const sortExplicitlySetRef = useRef(false);
  const didApplyProximityDefaultRef = useRef(false);
  const locationOptions = useMemo<SearchableSelectOption[]>(() => {
    // Determine if user has a preferred state (Lagos or Oyo)
    const userPreferredState = user?.location?.includes("Lagos")
      ? "Lagos"
      : user?.location?.includes("Oyo")
        ? "Oyo"
        : "Lagos"; // Default to Lagos

    const options: SearchableSelectOption[] = [
      {
        value: "",
        label: "All Locations",
        group: "General",
      },
    ];

    // Choose right LGAs and neighborhoods based on preferred state
    const lgas = userPreferredState === "Lagos" ? LAGOS_LGAS : OYO_LGAS;
    const neighborhoods =
      userPreferredState === "Lagos" ? LAGOS_NEIGHBORHOODS : OYO_NEIGHBORHOODS;

    // Add all LGAs from the user's preferred state
    lgas.forEach((lga) => {
      options.push({
        value: lga.name,
        label: lga.name,
        group: `${userPreferredState} LGAs`,
      });
    });

    // Add all neighborhoods organized by their LGA
    lgas.forEach((lga) => {
      // Get neighborhoods for this LGA
      const lgaNeighborhoods = neighborhoods.filter((n) => n.parent === lga.id);

      if (lgaNeighborhoods.length > 0) {
        lgaNeighborhoods.forEach((neighborhood) => {
          options.push({
            value: `${neighborhood.name}, ${lga.name}`, // Make value unique by including LGA
            label: neighborhood.name,
            group: `${lga.name}`,
          });
        });
      }
    });

    return options;
  }, [user]);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [isMounted, setIsMounted] = useState(false);
  const [priceRange, setPriceRange] = useState<PriceRange>({
    min: "",
    max: "",
  });
  const [isCalculatingDistances, setIsCalculatingDistances] = useState(false);
  const [processedItems, setProcessedItems] = useState<Item[]>([]);
  const [showLoginModal, setShowLoginModal] = useState(false);

  const tierContext = useMemo(() => {
    if (!userLocation) return null;
    const { state: userState, lga: userLGA } = getUserStateAndLGA({
      areaName: userLocation.areaName ?? "",
      formattedAddress: userLocation.formattedAddress,
    });

    return {
      areaName: userLocation.areaName ?? "",
      state: userState,
      lga: userLGA,
    };
  }, [userLocation]);

  // Debounce distance processing so rapid changes to items/location/sort coalesce
  const distanceProcessTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Distance-processing debug tracking
  const distanceRunSeqRef = useRef(0);
  const distanceInFlightRef = useRef(0);

  const userLocationKey = useMemo(() => {
    if (!userLocation) return "none";
    const area = userLocation.areaName ?? "";
    const lat = userLocation.coordinates?.lat ?? "";
    const lng = userLocation.coordinates?.lng ?? "";
    return `${area}:${lat}:${lng}`;
  }, [userLocation]);

  // State used to bias the unfiltered browse fetch toward the buyer's state (R5b).
  // Null unless the buyer has a real location — getUserStateAndLGA defaults to Lagos,
  // which must not be applied to locationless buyers (keeps flat global newest-N).
  const fetchUserState = useMemo(
    () =>
      userLocation?.areaName?.trim() && tierContext ? tierContext.state : null,
    [userLocation, tierContext],
  );

  // Memoize the fetch key based on filters - prevents unnecessary fetches.
  // Pagination is intentionally NOT part of the key: "Load more" pages a
  // client-side slice of the already-fetched pool, so it never refetches.
  const fetchKey = useMemo(() => {
    return JSON.stringify({
      category: selectedCategory,
      location: selectedLocation,
      search: searchQuery,
      priceMin: priceRange.min ? parseInt(priceRange.min) : 0,
      priceMax: priceRange.max ? parseInt(priceRange.max) : 3000000,
      // Only the unfiltered browse branch uses this, but keying on it keeps the
      // state-prioritized pool (R5b) correct when the buyer's state changes.
      userState: fetchUserState,
    });
  }, [
    selectedCategory,
    selectedLocation,
    searchQuery,
    fetchUserState,
    priceRange.min,
    priceRange.max,
  ]);

  // Use SWR for data fetching with automatic caching and revalidation
  const {
    data: itemsData,
    error,
    isValidating,
  } = useSWR(
    fetchKey,
    async () => {
      try {
        let baseItems: Item[] = [];

        if (searchQuery) {
          baseItems = await searchItems(searchQuery);

          if (selectedCategory) {
            baseItems = baseItems.filter(
              (item) =>
                item.category === selectedCategory.categoryId &&
                (!selectedCategory.subcategoryId ||
                  item.subcategory === selectedCategory.subcategoryId),
            );
          }

          if (selectedLocation) {
            baseItems = baseItems.filter((item) =>
              item.location
                .toLowerCase()
                .includes(selectedLocation.toLowerCase()),
            );
          }
        } else if (selectedCategory) {
          // Create a CategorySelection object for the API call
          baseItems = await getItemsByCategory({
            categoryId: selectedCategory.categoryId,
            subcategoryId: selectedCategory.subcategoryId,
            categoryName: categoryLabels[selectedCategory.categoryId],
            subcategoryName: selectedCategory.subcategoryName,
          });

          if (selectedLocation) {
            baseItems = baseItems.filter((item) =>
              item.location
                .toLowerCase()
                .includes(selectedLocation.toLowerCase()),
            );
          }
        } else if (selectedLocation) {
          baseItems = await getItemsByLocation(selectedLocation);
        } else {
          // Limit items for the market page to prevent performance issues.
          // Users can filter/search to find more specific items; the cap is
          // surfaced honestly via `catalogCapped` (D3).
          baseItems = await getApprovedItems(
            CATALOG_BROWSE_LIMIT,
            fetchUserState,
          );
        }

        // Apply price filter
        if (priceRange.min || priceRange.max) {
          baseItems = baseItems.filter((item) => {
            const minPrice = priceRange.min ? parseInt(priceRange.min) : 0;
            const maxPrice = priceRange.max
              ? parseInt(priceRange.max)
              : 3000000;
            return item.price >= minPrice && item.price <= maxPrice;
          });
        }

        // Return the full price-filtered pool. Eligibility filtering (location
        // reach), sorting, and pagination all happen downstream so the count
        // and the rendered list stay in agreement (R1/R2).
        return {
          items: baseItems,
          total: baseItems.length,
        };
      } catch (error) {
        console.error("Error fetching items:", error);
        throw error;
      }
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 10000,
      shouldRetryOnError: true,
      errorRetryCount: 3,
    },
  );

  // Stable key so the distance effect only runs when the logical item set changes
  const itemsKey = useMemo(() => {
    const items = itemsData?.items;
    if (!items || items.length === 0) return "none";
    const ids = items.map((item) => item.id).join(",");
    return `${items.length}:${ids}`;
  }, [itemsData?.items]);

  // Single source of truth for the DISPLAYED set (R1/R2): the fetched pool
  // narrowed to what the buyer is allowed to see (location reach). The results
  // count, empty-state decision, and pagination all derive from this — never
  // from the raw fetch pool — so the number always matches what renders.
  // Distance-independent, so it resolves synchronously (no flash on the count).
  const eligibleItems = useMemo(
    () =>
      selectEligibleMarketItems(itemsData?.items ?? [], {
        userLocation,
        sortBy,
        includeOtherLocationTier,
        granularity: homepageDiscoveryGranularity,
        autoWiden: homepageDiscoveryAutoWidenEnabled,
        minCount: ITEMS_PER_PAGE,
      }),
    [
      itemsData?.items,
      userLocation,
      sortBy,
      includeOtherLocationTier,
      homepageDiscoveryGranularity,
      homepageDiscoveryAutoWidenEnabled,
    ],
  );

  // The count buyers see: eligible items, not the raw fetch pool.
  const displayedTotal = eligibleItems.length;

  // Derived: more pages remain when the eligible set exceeds what's shown.
  const hasMore = page * ITEMS_PER_PAGE < eligibleItems.length;

  const availabilitySortBoostOptions = useMemo<AvailabilitySortBoostOptions>(
    () => ({
      sortBoostEnabled: buyerAvailabilityConfirmedSortBoostEnabled,
      windowDays: buyerAvailabilityConfirmedWindowDays,
    }),
    [
      buyerAvailabilityConfirmedSortBoostEnabled,
      buyerAvailabilityConfirmedWindowDays,
    ],
  );

  const mainTotal = itemsData ? displayedTotal : null;
  const mainItemIds = eligibleItems.map((i) => i.id);
  const shouldFetchRelated =
    !!searchQuery.trim() &&
    mainTotal !== null &&
    mainTotal < 4 &&
    !isValidating;
  const relatedKey = shouldFetchRelated
    ? `related-${searchQuery}-${mainTotal}-${mainItemIds.join(",")}`
    : null;
  const { data: relatedItems = [] } = useSWR(
    relatedKey,
    () => getRelatedSearchItems(searchQuery, mainItemIds, 12),
    { revalidateOnFocus: false, dedupingInterval: 60000 },
  );

  // R3: explicit, buyer-triggered pagination. Replaces the fragile
  // IntersectionObserver infinite scroll with a "Load more" action so loading
  // is predictable and never races with distance re-processing. Guarded so a
  // click while a fetch is in flight (or at the end of the pool) is a no-op.
  const loadMore = useCallback(() => {
    if (!hasMore || isValidating) return;
    setPage((prevPage) => prevPage + 1);
  }, [hasMore, isValidating]);

  // Process items with distance calculations
  useEffect(() => {
    const processItems = async () => {
      if (!itemsData?.items) {
        setProcessedItems([]);
        return;
      }

      let filtered = [...itemsData.items];

      // Eligibility (location reach) is owned by selectEligibleMarketItems so
      // the rendered order here matches the count/empty source of truth.
      const applyDiscoveryReach = (items: Item[]): Item[] =>
        selectEligibleMarketItems(items, {
          userLocation,
          sortBy,
          includeOtherLocationTier,
          granularity: homepageDiscoveryGranularity,
          autoWiden: homepageDiscoveryAutoWidenEnabled,
          minCount: ITEMS_PER_PAGE,
        });

      distanceRunSeqRef.current += 1;
      distanceInFlightRef.current += 1;

      // Skip redundant runs for the same logical inputs.
      // This prevents React StrictMode/dev re-runs from duplicating distance-matrix calls.
      const distanceSignature = `${itemsKey}|${userLocationKey}|${sortBy}`;
      const globalInFlight = getGlobalMarketDistanceInFlight();
      const globalLastSignature = getGlobalMarketLastDistanceSignature();

      if (!globalInFlight && globalLastSignature === distanceSignature) {
        distanceInFlightRef.current -= 1;
        return;
      }

      if (globalInFlight) {
        distanceInFlightRef.current -= 1;
        return;
      }

      setGlobalMarketDistanceInFlight(true);
      setGlobalMarketLastDistanceSignature(distanceSignature);

      // If sort depends on location but we don't have coordinates yet, just do a simple newest sort for now.
      // The effect will re-run once location is available (userLocationKey changes).
      if (
        (sortBy === "newest" || sortBy === "proximity") &&
        !userLocation?.coordinates
      ) {
        filtered.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        setProcessedItems(applyDiscoveryReach(filtered));
        distanceInFlightRef.current -= 1;
        return;
      }

      // Apply sorting based on sortBy parameter
      if (sortBy === "newest") {
        // When user has location: area → LGA → state → other, then by distance within each tier
        if (userLocation?.coordinates) {
          setIsCalculatingDistances(true);

          // R4 progressive render: paint the grid in tier order immediately so
          // buyers see results without waiting on the distance-matrix round-trip.
          // The within-tier order is refined below once distances resolve.
          setProcessedItems(
            applyDiscoveryReach(
              orderItemsByTier(filtered, {
                areaName: userLocation.areaName ?? "",
                formattedAddress: userLocation.formattedAddress,
              }),
            ),
          );

          try {
            const itemsWithCoords = filtered.filter((item) => item.coordinates);
            const itemsWithoutCoords = filtered.filter(
              (item) => !item.coordinates,
            );

            if (itemsWithCoords.length > 0) {
              const { state: userState, lga: userLGA } = getUserStateAndLGA({
                areaName: userLocation.areaName ?? "",
                formattedAddress: userLocation.formattedAddress,
              });
              const areaName = userLocation.areaName ?? "";
              const getTier = (item: Item): LocationTier =>
                getLocationTier(
                  item.location ?? "",
                  areaName,
                  userState,
                  userLGA,
                );

              // Partition by tier first (no distance), then cap how many get distance calculation
              const byTierCoords: Record<LocationTier, Item[]> = {
                area: [],
                lga: [],
                state: [],
                other: [],
              };
              for (const item of itemsWithCoords) {
                byTierCoords[getTier(item)].push(item);
              }

              // Make tier ordering deterministic (prevents different slice results across reruns).
              (["area", "lga", "state", "other"] as const).forEach((tier) => {
                byTierCoords[tier].sort((a, b) =>
                  String(a.id).localeCompare(String(b.id)),
                );
              });

              const orderedCoords = [
                ...byTierCoords.area,
                ...byTierCoords.lga,
                ...byTierCoords.state,
                ...byTierCoords.other,
              ];
              const coordsToUse = orderedCoords.slice(
                0,
                MARKET_MAX_ITEMS_FOR_DISTANCE,
              );
              // Ensure deterministic destination ordering to maximize in-flight deduping.
              coordsToUse.sort((a, b) =>
                String(a.id).localeCompare(String(b.id)),
              );
              const restCoordsNoDistance = orderedCoords.slice(
                MARKET_MAX_ITEMS_FOR_DISTANCE,
              );

              const distances = await calculateDistancesAsync(
                userLocation.coordinates,
                coordsToUse.map((item) => item.coordinates!),
              );

              const itemsWithDistance = coordsToUse.map((item, index) => ({
                ...item,
                distance: distances[index],
              }));

              const byTier: Record<LocationTier, ProcessedItem[]> = {
                area: [],
                lga: [],
                state: [],
                other: [],
              };
              for (const item of itemsWithDistance) {
                byTier[getTier(item)].push(item as ProcessedItem);
              }

              const sortByDistance = (a: ProcessedItem, b: ProcessedItem) =>
                compareWithinTierWithAvailabilityBoost(
                  a,
                  b,
                  (left, right) =>
                    (left.distance ?? Infinity) - (right.distance ?? Infinity),
                  availabilitySortBoostOptions,
                );
              for (const tier of ["area", "lga", "state", "other"] as const) {
                byTier[tier].sort(sortByDistance);
              }

              const noCoordsByTier: Record<LocationTier, ProcessedItem[]> = {
                area: [],
                lga: [],
                state: [],
                other: [],
              };
              for (const item of itemsWithoutCoords) {
                const tier = getTier(item);
                noCoordsByTier[tier].push({
                  ...item,
                  distance: Infinity,
                });
              }

              const sortNoCoordsByCreatedAt = (
                a: ProcessedItem,
                b: ProcessedItem,
              ) =>
                compareWithinTierWithAvailabilityBoost(
                  a,
                  b,
                  (left, right) =>
                    right.createdAt.getTime() - left.createdAt.getTime(),
                  availabilitySortBoostOptions,
                );
              for (const tier of ["area", "lga", "state", "other"] as const) {
                noCoordsByTier[tier].sort(sortNoCoordsByCreatedAt);
              }

              const restNoDistance = restCoordsNoDistance.map((item) => ({
                ...item,
                distance: undefined,
              }));

              filtered = [
                ...byTier.area,
                ...byTier.lga,
                ...byTier.state,
                ...byTier.other,
                ...restNoDistance,
                ...noCoordsByTier.area,
                ...noCoordsByTier.lga,
                ...noCoordsByTier.state,
                ...noCoordsByTier.other,
              ];
            }
          } catch (error) {
            console.error(
              "[MARKET] Error calculating distances for newest sort:",
              error,
            );
            filtered.sort(
              (a, b) => b.createdAt.getTime() - a.createdAt.getTime(),
            );
          } finally {
            setIsCalculatingDistances(false);
          }
        } else {
          // Without user location, just sort by date
          filtered.sort(
            (a, b) => b.createdAt.getTime() - a.createdAt.getTime(),
          );
        }
      } else if (sortBy === "proximity" && userLocation?.coordinates) {
        setIsCalculatingDistances(true);

        // R4 progressive render: paint tier order immediately, refine below.
        setProcessedItems(
          applyDiscoveryReach(
            orderItemsByTier(filtered, {
              areaName: userLocation.areaName ?? "",
              formattedAddress: userLocation.formattedAddress,
            }),
          ),
        );

        try {
          const itemsWithCoords = filtered.filter((item) => item.coordinates);
          const itemsWithoutCoords = filtered.filter(
            (item) => !item.coordinates,
          );

          if (itemsWithCoords.length > 0) {
            const { state: userState, lga: userLGA } = getUserStateAndLGA({
              areaName: userLocation.areaName ?? "",
              formattedAddress: userLocation.formattedAddress,
            });
            const areaName = userLocation.areaName ?? "";
            const getTier = (item: Item): LocationTier =>
              getLocationTier(
                item.location ?? "",
                areaName,
                userState,
                userLGA,
              );

            const byTierCoords: Record<LocationTier, Item[]> = {
              area: [],
              lga: [],
              state: [],
              other: [],
            };
            for (const item of itemsWithCoords) {
              byTierCoords[getTier(item)].push(item);
            }

            // Make tier ordering deterministic (prevents different slice results across reruns).
            (["area", "lga", "state", "other"] as const).forEach((tier) => {
              byTierCoords[tier].sort((a, b) =>
                String(a.id).localeCompare(String(b.id)),
              );
            });

            const orderedCoords = [
              ...byTierCoords.area,
              ...byTierCoords.lga,
              ...byTierCoords.state,
              ...byTierCoords.other,
            ];
            const coordsToUse = orderedCoords.slice(
              0,
              MARKET_MAX_ITEMS_FOR_DISTANCE,
            );
            // Ensure deterministic destination ordering to maximize in-flight deduping.
            coordsToUse.sort((a, b) =>
              String(a.id).localeCompare(String(b.id)),
            );
            const restCoordsNoDistance = orderedCoords.slice(
              MARKET_MAX_ITEMS_FOR_DISTANCE,
            );

            const distances = await calculateDistancesAsync(
              userLocation.coordinates,
              coordsToUse.map((item) => item.coordinates!),
            );

            const itemsWithDistance = coordsToUse.map((item, index) => ({
              ...item,
              distance: distances[index],
            }));

            const byTier: Record<LocationTier, ProcessedItem[]> = {
              area: [],
              lga: [],
              state: [],
              other: [],
            };
            for (const item of itemsWithDistance) {
              byTier[getTier(item)].push(item as ProcessedItem);
            }

            const sortByDistance = (a: ProcessedItem, b: ProcessedItem) =>
              (a.distance ?? Infinity) - (b.distance ?? Infinity);
            for (const tier of ["area", "lga", "state", "other"] as const) {
              byTier[tier].sort(sortByDistance);
            }

            const noCoordsByTier: Record<LocationTier, ProcessedItem[]> = {
              area: [],
              lga: [],
              state: [],
              other: [],
            };
            for (const item of itemsWithoutCoords) {
              const tier = getTier(item);
              noCoordsByTier[tier].push({ ...item, distance: Infinity });
            }

            const restNoDistance = restCoordsNoDistance.map((item) => ({
              ...item,
              distance: undefined,
            }));

            filtered = [
              ...byTier.area,
              ...byTier.lga,
              ...byTier.state,
              ...byTier.other,
              ...restNoDistance,
              ...noCoordsByTier.area,
              ...noCoordsByTier.lga,
              ...noCoordsByTier.state,
              ...noCoordsByTier.other,
            ];
          }
        } catch (error) {
          console.error(
            "[MARKET] Error calculating distances for proximity sort:",
            error,
          );
        } finally {
          setIsCalculatingDistances(false);
        }
      } else {
        // For other sort options, calculate distances for display only (capped to limit API calls)
        if (userLocation?.coordinates) {
          try {
            const itemsWithCoords = filtered.filter((item) => item.coordinates);

            if (itemsWithCoords.length > 0) {
              const coordsToUse = itemsWithCoords.slice(
                0,
                MARKET_MAX_ITEMS_FOR_DISTANCE,
              );

              const distances = await calculateDistancesAsync(
                userLocation.coordinates,
                coordsToUse.map((item) => item.coordinates!),
              );

              let distanceIndex = 0;
              filtered = filtered.map((item) => {
                if (item.coordinates && distanceIndex < distances.length) {
                  const distance = distances[distanceIndex++];
                  return { ...item, distance };
                }
                return item;
              });
            }
          } catch (error) {
            console.warn(
              "[MARKET] Error calculating distances for display:",
              error,
            );
          }
        }
      }

      setProcessedItems(applyDiscoveryReach(filtered));

      distanceInFlightRef.current -= 1;
      setGlobalMarketDistanceInFlight(false);
    };

    // If there are no items, clear state and cancel any pending work
    if (!itemsData?.items || itemsData.items.length === 0) {
      queueMicrotask(() => {
        setProcessedItems([]);
        setIsCalculatingDistances(false);
      });
      if (distanceProcessTimeoutRef.current) {
        clearTimeout(distanceProcessTimeoutRef.current);
        distanceProcessTimeoutRef.current = null;
      }
      return;
    }

    // Debounce the heavy distance processing to avoid multiple runs on rapid changes
    if (distanceProcessTimeoutRef.current) {
      clearTimeout(distanceProcessTimeoutRef.current);
    }
    distanceProcessTimeoutRef.current = setTimeout(() => {
      void processItems();
    }, 250);

    return () => {
      if (distanceProcessTimeoutRef.current) {
        clearTimeout(distanceProcessTimeoutRef.current);
        distanceProcessTimeoutRef.current = null;
      }
    };
    // We intentionally depend on stable keys rather than the raw objects/arrays
    // to avoid re-running the effect when references change but contents do not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    itemsKey,
    userLocationKey,
    sortBy,
    calculateDistancesAsync,
    includeOtherLocationTier,
    homepageDiscoveryGranularity,
    homepageDiscoveryAutoWidenEnabled,
    buyerAvailabilityConfirmedSortBoostEnabled,
    buyerAvailabilityConfirmedWindowDays,
  ]);

  // Rendered slice: a page of the sorted set. `processedItems` carries the
  // distance sort/order; before it settles we fall back to `eligibleItems`
  // (same membership, fetch order) so the grid is never blank while the count
  // already reads correctly. Sorting/pagination never change membership, so
  // the visible count stays equal to `displayedTotal`.
  const filteredItems = useMemo(() => {
    const source = processedItems.length > 0 ? processedItems : eligibleItems;
    return source.slice(0, page * ITEMS_PER_PAGE);
  }, [processedItems, eligibleItems, page]);

  const locationEndBannerInsertIndex = useMemo(() => {
    if (!tierContext) return null;
    if (!userLocation?.coordinates) return null;
    if (!(sortBy === "newest" || sortBy === "proximity")) return null;
    if (filteredItems.length === 0) return null;

    // Find the last index where the item's tier is area/lga/state.
    // Insert a banner right after that, so users know they've moved
    // past their location tiers and into other locations.
    let lastLocalIndex = -1;
    for (let i = 0; i < filteredItems.length; i += 1) {
      const tier = getLocationTier(
        filteredItems[i].location ?? "",
        tierContext.areaName,
        tierContext.state,
        tierContext.lga,
      );
      if (tier !== "other") lastLocalIndex = i;
    }

    if (lastLocalIndex >= 0 && lastLocalIndex < filteredItems.length - 1) {
      return lastLocalIndex + 1;
    }

    return null;
  }, [filteredItems, tierContext, userLocation?.coordinates, sortBy]);

  // Reset page when filters change
  useEffect(() => {
    queueMicrotask(() => {
      setPage(1);
    });
  }, [selectedCategory, selectedLocation, searchQuery, sortBy, priceRange]);

  // D3: an unfiltered default browse that hits the fetch cap is only showing a
  // subset of the catalog — surface that honestly in the results bar.
  const catalogCapped = useMemo(() => {
    const isUnfilteredBrowse =
      !searchQuery.trim() &&
      !selectedCategory &&
      !selectedLocation.trim() &&
      !priceRange.min.trim() &&
      !priceRange.max.trim();
    return (
      isUnfilteredBrowse && (itemsData?.total ?? 0) >= CATALOG_BROWSE_LIMIT
    );
  }, [
    searchQuery,
    selectedCategory,
    selectedLocation,
    priceRange.min,
    priceRange.max,
    itemsData?.total,
  ]);

  const showRecentlyConfirmedShelf = useMemo(() => {
    if (page !== 1) return false;
    if (searchQuery.trim()) return false;
    if (selectedCategory) return false;
    if (selectedLocation.trim()) return false;
    if (priceRange.min.trim() || priceRange.max.trim()) return false;
    if (!userLocation?.areaName?.trim()) return false;
    return true;
  }, [
    page,
    searchQuery,
    selectedCategory,
    selectedLocation,
    priceRange.min,
    priceRange.max,
    userLocation,
  ]);

  // Memoized handlers
  const handleLocationSelect = useCallback(
    (location: string) => {
      setSelectedLocation(location);
      const query = serializeLocationParam(searchParams.toString(), location);
      router.push(`/market?${query}`);
    },
    [searchParams, router],
  );

  const handleCategorySelect = useCallback(
    (category: CategorySelection | null) => {
      setSelectedCategory(category);
      const query = serializeCategoryParams(searchParams.toString(), category);
      router.push(query ? `/market?${query}` : "/market");
    },
    [searchParams, router],
  );

  const handleSortChange = useCallback(
    (sort: string) => {
      if (sort === "proximity" && !isAuthenticated) {
        setShowLoginModal(true);
        return;
      }
      sortExplicitlySetRef.current = true;
      setSortBy(sort);

      // Use replaceState to update URL without causing navigation
      const query = serializeSortParam(searchParams.toString(), sort);
      window.history.replaceState({}, "", `?${query}`);
    },
    [searchParams, isAuthenticated],
  );

  const handleViewModeChange = useCallback((mode: "grid" | "list") => {
    setViewMode(mode);
  }, []);

  const handlePriceRangeChange = useCallback(
    (range: PriceRange) => {
      setPriceRange(range);

      // Use replaceState to update URL without causing navigation
      const query = serializePriceParams(searchParams.toString(), range);
      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}?${query}`,
      );
    },
    [searchParams],
  );

  // Mark component as mounted after hydration
  useEffect(() => {
    queueMicrotask(() => {
      setIsMounted(true);
    });
  }, []);

  // Update viewMode based on window size only after client-side hydration
  useEffect(() => {
    if (isMounted) {
      const handleResize = () => {
        setViewMode(window.innerWidth < 768 ? "list" : "grid");
      };

      // Set initial value
      handleResize();

      // Add event listener for future resizes
      window.addEventListener("resize", handleResize);
      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }
  }, [isMounted]);

  // Initialize from URL params (including category/subcategory) once.
  const didInitFromUrlRef = useRef(false);
  useEffect(() => {
    if (didInitFromUrlRef.current) return;

    const initial = readInitialMarketState(
      new URLSearchParams(searchParams.toString()),
      activeCategoryStructure,
    );

    if (initial.location) {
      queueMicrotask(() => {
        setSelectedLocation(initial.location!);
      });
    }

    if (initial.category) {
      queueMicrotask(() => {
        setSelectedCategory(initial.category);
      });
    }

    if (initial.search) {
      queueMicrotask(() => {
        setSearchQuery(initial.search!);
      });
    }

    if (initial.sort) {
      sortExplicitlySetRef.current = true;
      queueMicrotask(() => {
        setSortBy(initial.sort!);
      });
    }

    if (initial.priceRange) {
      queueMicrotask(() => {
        setPriceRange(initial.priceRange!);
      });
    }
    didInitFromUrlRef.current = true;
  }, [searchParams, activeCategoryStructure]);

  // D2: default authed buyers with coordinates to nearest (proximity). Guests
  // and users without coordinates stay on tier-boosted `newest`. Never overrides
  // an explicit URL param or manual toggle, and applies at most once.
  useEffect(() => {
    if (didApplyProximityDefaultRef.current) return;
    if (sortExplicitlySetRef.current) return;
    if (!isAuthenticated) return;
    if (!userLocation?.coordinates) return;
    didApplyProximityDefaultRef.current = true;
    queueMicrotask(() => {
      setSortBy("proximity");
    });
  }, [isAuthenticated, userLocation?.coordinates]);

  useEffect(() => {
    const q = searchQuery.trim();
    if (!q) {
      lastTrackedMetaSearchRef.current = null;
      return;
    }
    if (lastTrackedMetaSearchRef.current === q) return;
    lastTrackedMetaSearchRef.current = q;
    trackMetaSearch(q);
    trackClaritySearch(q);
  }, [searchQuery]);

  // Handle search
  const handleSearch = useCallback(
    async (query: string): Promise<SearchResponse> => {
      setSearchQuery(query);
      const nextQuery = serializeSearchParam(searchParams.toString(), query);
      router.push(`/market?${nextQuery}`);

      // Keep a single source of truth for market list data:
      // SWR re-fetches via itemsAPI.searchItems() when searchQuery changes.
      return { items: [], total: 0 };
    },
    [searchParams, router],
  );

  const navigateToRequestItem = useCallback(() => {
    if (!user) {
      router.push("/login?redirectTo=" + encodeURIComponent("/request-item"));
    } else {
      router.push("/request-item");
    }
  }, [user, router]);

  const onUseDefaultSort = useCallback(() => {
    setSortBy("proximity");
  }, []);

  // Phase 5: which empty-state variant applies when a browse renders nothing.
  // Owner-side so MarketView stays presentation-only (no scattered JSX rules).
  const emptyStateVariant = useMemo<"search" | "filtered" | "plain">(() => {
    if (searchQuery.trim()) return "search";
    if (
      selectedCategory ||
      selectedLocation.trim() ||
      priceRange.min.trim() ||
      priceRange.max.trim()
    ) {
      return "filtered";
    }
    return "plain";
  }, [
    searchQuery,
    selectedCategory,
    selectedLocation,
    priceRange.min,
    priceRange.max,
  ]);

  // Show the related shelf inside the empty state only on a zero-result search
  // that actually has related suggestions (keeps the existing fetch guard).
  const showRelatedInEmptyState =
    !!searchQuery.trim() && relatedItems.length > 0;

  // Phase 4: active-filter chips derived from browse state (single owner).
  const activeFilterChips = useMemo(
    () =>
      buildActiveFilterChips({
        searchQuery,
        selectedCategory,
        selectedLocation,
        priceRange,
      }),
    [searchQuery, selectedCategory, selectedLocation, priceRange],
  );

  // Remove a single active filter. Reuses the existing per-filter handlers so
  // URL <-> state mutation stays single-path (browseState + useMarketBrowse).
  const handleRemoveFilter = useCallback(
    (kind: MarketFilterChipKind) => {
      switch (kind) {
        case "search":
          void handleSearch("");
          break;
        case "category":
          handleCategorySelect(null);
          break;
        case "subcategory":
          if (selectedCategory) {
            handleCategorySelect({
              ...selectedCategory,
              subcategoryId: "",
              subcategoryName: "",
            });
          }
          break;
        case "location":
          handleLocationSelect("");
          break;
        case "price":
          handlePriceRangeChange({ min: "", max: "" });
          break;
      }
    },
    [
      handleSearch,
      handleCategorySelect,
      handleLocationSelect,
      handlePriceRangeChange,
      selectedCategory,
    ],
  );

  // Phase 6 / 6.2: nested category surface. Quick picks stay visible when a
  // category is active; subcategory picks refine underneath. Selection still
  // flows through `handleCategorySelect` — single URL/state path.
  const categoryQuickPicks = useMemo(
    () =>
      buildVisibleCategoryChips(
        buildCategoryQuickPicks(activeCategoryStructure),
        selectedCategory,
      ),
    [activeCategoryStructure, selectedCategory],
  );
  const subcategoryPicks = useMemo(
    () => buildSubcategoryPicks(activeCategoryStructure, selectedCategory),
    [activeCategoryStructure, selectedCategory],
  );

  // Clear every filter at once (D4). Preserves non-filter params like `sort`.
  const handleClearAllFilters = useCallback(() => {
    setSearchQuery("");
    setSelectedCategory(null);
    setSelectedLocation("");
    setPriceRange({ min: "", max: "" });
    const query = serializeClearAllFilters(searchParams.toString());
    router.push(query ? `/market?${query}` : "/market");
  }, [searchParams, router]);

  return {
    // settings / banner
    settingsLoading,
    nearMePlatformEnabled: newListingNearMeNotificationsEnabled,
    // filter state
    selectedCategory,
    selectedLocation,
    searchQuery,
    sortBy,
    priceRange,
    viewMode,
    locationOptions,
    activeCategoryStructure,
    defaultSearchValue: searchParams.get("search") || "",
    // data
    itemsData,
    // Displayed count = eligible set (matches what renders). Use this for the
    // results summary and empty-state decision, never `itemsData.total`.
    displayedTotal,
    error,
    isValidating,
    filteredItems,
    relatedItems,
    // derived UI
    isCalculatingDistances,
    // Area/state context for the benefit-first header subtitle (Phase 2).
    // Reuses tierContext so location derivation stays single-owner.
    nearYouLocationContext: tierContext,
    // Honest catalog-cap flag for the results bar (Phase 3, D3).
    catalogCapped,
    // Active-filter chips + reset handlers (Phase 4, D4).
    activeFilterChips,
    // Empty/error recovery derivations (Phase 5, C6/C7).
    emptyStateVariant,
    showRelatedInEmptyState,
    // Single category surface: popular top-level picks + subcategory drill-down (Phase 6).
    categoryQuickPicks,
    subcategoryPicks,
    showRecentlyConfirmedShelf,
    locationEndBannerInsertIndex,
    hasMore,
    loadMore,
    isMounted,
    isLoadingLocation,
    // share
    itemShare,
    // quick-view preview modal (parity with homepage)
    itemPreview,
    // login modal
    showLoginModal,
    setShowLoginModal,
    onUseDefaultSort,
    // handlers
    handleLocationSelect,
    handleCategorySelect,
    handleSortChange,
    handleViewModeChange,
    handlePriceRangeChange,
    handleSearch,
    handleRemoveFilter,
    handleClearAllFilters,
    navigateToRequestItem,
  };
}

export type UseMarketBrowseResult = ReturnType<typeof useMarketBrowse>;
